(function () {
  if (customElements.get('ce-predictive-search') !== undefined) {
    return;
  }

  class PredictiveSearch extends HTMLParsedElement {
    constructor() {
      super();

      this.queuedAttributeChangedCallbackParams = [];

      this.selectors = {
        menuContainer: '[data-predictive-search-menu-container]',
        menus: '[data-predictive-search-menu]',
        input: '[data-predictive-search-input]',
        panel: '[data-predictive-search-panel]',
        results: '[data-predictive-search-results]',
        link: '[data-predictive-search-link]',
        status: '[data-predictive-search-status]',
        toaster: 'ce-toaster',
      };

      this.elements = null;
      this.menuHidden = false;

      this.abortController = null;
      this.timeoutDebounce = null;

      this.resources = [];

      this.handleStickyScrollHeaderScrollDirection = this.handleStickyScrollHeaderScrollDirection.bind(this);
      this.handleInputInput = this.handleInputInput.bind(this);
      this.handleFocusInput = this.handleFocusInput.bind(this);
      this.handleBlurInput = this.handleBlurInput.bind(this);
      this.handleMouseEnterPanel = this.handleMouseEnterPanel.bind(this);
      this.handleMouseLeavePanel = this.handleMouseLeavePanel.bind(this);
      this.handleKeyDown = this.handleKeyDown.bind(this);
    }

    get hasKeyboardFocus() {
      return this.getAttribute('has-keyboard-focus') === 'true';
    }

    set hasKeyboardFocus(value) {
      this.setAttribute('has-keyboard-focus', value);
    }

    get hasMouseFocus() {
      return this.getAttribute('has-mouse-focus') === 'true';
    }

    set hasMouseFocus(value) {
      this.setAttribute('has-mouse-focus', value);
    }

    get hasResults() {
      return this.getAttribute('has-results') === 'true';
    }

    set hasResults(value) {
      this.setAttribute('has-results', value);
    }

    get selectedResourceId() {
      return this.getAttribute('selected-resource-id');
    }

    set selectedResourceId(value) {
      this.setAttribute('selected-resource-id', value);
    }

    static get observedAttributes() {
      return ['has-keyboard-focus', 'has-mouse-focus', 'has-results', 'selected-resource-id'];
    }

    attributeChangedCallback(...params) {
      if (!this.parsed) {
        this.queuedAttributeChangedCallbackParams.push(params);
        return;
      }

      const [name, oldValue, newValue] = params;

      if (oldValue === newValue) {
        return;
      }

      switch (name) {
        case 'has-keyboard-focus':
          if (newValue !== null) {
            // Antigos: le voci del menu si nascondono solo se c'è del testo, non al solo focus
            this.updateMenu();

            if ((newValue === 'true' || this.hasMouseFocus) && this.hasResults) {
              this.elements.panel.style.display = 'block';
            } else {
              this.elements.panel.style.display = 'none';
            }
          }

          break;
        case 'has-mouse-focus':
          if (newValue !== null) {
            // Antigos: le voci del menu si nascondono solo se c'è del testo, non al solo focus
            this.updateMenu();

            if ((newValue === 'true' || this.hasKeyboardFocus) && this.hasResults) {
              this.elements.panel.style.display = 'block';
            } else {
              this.elements.panel.style.display = 'none';
            }
          }

          break;
        case 'has-results':
          if (newValue !== null) {
            if (newValue === 'true' && (this.hasKeyboardFocus || this.hasMouseFocus)) {
              this.elements.panel.style.display = 'block';
            } else {
              this.elements.panel.style.display = 'none';
            }
          }

          break;
        case 'selected-resource-id':
          if (oldValue !== null && oldValue !== '') {
            const oldResource = this.elements.results.querySelector(`#${oldValue}`);
            oldResource?.setAttribute('aria-selected', 'false');
            this.elements.status.textContent = '';
            this.elements.input.removeAttribute('aria-activedescendant');
          }

          if (newValue !== null && newValue !== '') {
            const newResource = this.elements.results.querySelector(`#${newValue}`);
            newResource.setAttribute('aria-selected', 'true');
            this.elements.status.textContent = newResource.textContent;
            this.elements.input.setAttribute('aria-activedescendant', newValue);
          }

          break;
      }
    }

    parsedCallback() {
      this.elements = {
        menuContainer: document.querySelector(this.selectors.menuContainer),
        menus: [...document.querySelectorAll(this.selectors.menus)],
        input: this.querySelector(this.selectors.input),
        panel: this.querySelector(this.selectors.panel),
        results: this.querySelector(this.selectors.results),
        status: this.querySelector(this.selectors.status),
        toaster: document.querySelector(this.selectors.toaster),
      };

      document.addEventListener('ce-sticky-scroll-header:scroll-direction', this.handleStickyScrollHeaderScrollDirection);
      this.elements.input.addEventListener('input', this.handleInputInput);
      this.elements.input.addEventListener('keydown', this.handleKeyDown);
      this.elements.input.addEventListener('focus', this.handleFocusInput);
      this.elements.input.addEventListener('blur', this.handleBlurInput);
      this.elements.panel.addEventListener('mouseenter', this.handleMouseEnterPanel);
      this.elements.panel.addEventListener('mouseleave', this.handleMouseLeavePanel);

      for (let params of this.queuedAttributeChangedCallbackParams) {
        this.attributeChangedCallback(...params);
      }
    }

    disconnectedCallback() {
      document.removeEventListener('ce-sticky-scroll-header:scroll-direction', this.handleStickyScrollHeaderScrollDirection);
      this.elements.input.removeEventListener('input', this.handleInputInput);
      this.elements.input.removeEventListener('keydown', this.handleKeyDown);
      this.elements.input.removeEventListener('focus', this.handleFocusInput);
      this.elements.input.removeEventListener('blur', this.handleBlurInput);
      this.elements.panel.removeEventListener('mouseenter', this.handleMouseEnterPanel);
      this.elements.panel.removeEventListener('mouseleave', this.handleMouseLeavePanel);
    }

    handleStickyScrollHeaderScrollDirection(event) {
      const scrollDirection = event.detail;

      if (scrollDirection === 'down') {
        this.elements.panel.style.display = 'none';
      }
    }

    handleInputInput(event) {
      this.updateMenu();

      if (this.abortController !== null) {
        this.abortController.abort();
        this.abortController = null;
      }

      clearTimeout(this.timeoutDebounce);

      this.timeoutDebounce = setTimeout(() => {
        const terms = encodeURIComponent(event.target.value.trim());

        if (terms === '') {
          this.hasResults = false;

          this.resources = [];
          this.selectedResourceId = '';

          return;
        }

        this.search(terms);
      }, 250);
    }

    handleKeyDown(event) {
      if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown' && event.key !== 'Enter') {
        return;
      }

      if (this.resources.length === 0) {
        return;
      }

      const i = this.resources.findIndex((e) => e.id === this.selectedResourceId);

      if (event.key === 'ArrowUp') {
        event.preventDefault();

        if (i > 0) {
          const previousResource = this.resources[i - 1];
          this.selectedResourceId = previousResource.id;
        } else {
          const lastResource = this.resources[this.resources.length - 1];
          this.selectedResourceId = lastResource.id;
        }
      }

      if (event.key === 'ArrowDown') {
        event.preventDefault();

        if (i !== -1 && i < this.resources.length - 1) {
          const nextResource = this.resources[i + 1];
          this.selectedResourceId = nextResource.id;
        } else {
          const firstResource = this.resources[0];
          this.selectedResourceId = firstResource.id;
        }
      }

      if (event.key === 'Enter') {
        const selectedResource = this.resources[i];
        selectedResource.click();
      }
    }

    handleFocusInput() {
      this.hasKeyboardFocus = true;
    }

    handleBlurInput() {
      this.hasKeyboardFocus = false;
    }

    handleMouseEnterPanel() {
      this.hasMouseFocus = true;
    }

    handleMouseLeavePanel() {
      this.hasMouseFocus = false;
    }

    async search(terms) {
      const resource = `${Themu.routes.predictiveSearch}?q=${terms}&section_id=predictive-search`;

      try {
        this.abortController = new AbortController();

        const response = await fetch(resource, { signal: this.abortController.signal });

        if (!response.ok) {
          throw Error;
        }

        const responseText = await response.text();

        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const innerHTML = html.querySelector('#shopify-section-predictive-search').innerHTML;

        if (innerHTML === '') {
          this.hasResults = false;

          this.resources = [];
          this.selectedResourceId = '';
        } else {
          this.elements.results.innerHTML = innerHTML;

          this.resources = Array.from(this.elements.results.querySelectorAll(this.selectors.link));
          this.selectedResourceId = '';

          this.hasResults = true;
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          this.elements.toaster.makeToast('danger', Themu.t.sections.predictive_search.error_message);
        }
      }
    }

    // Antigos: menu nascosto solo con la ricerca attiva e del testo nel campo.
    // Prima bastava il focus: all'apertura del menu mobile il focus finiva nella
    // ricerca e le voci sparivano.
    updateMenu() {
      const hide = (this.hasKeyboardFocus || this.hasMouseFocus) && this.elements.input.value.trim() !== '';

      if (hide === this.menuHidden) {
        return;
      }

      this.menuHidden = hide;

      if (hide) {
        this.hideMenu();
      } else {
        this.showMenu();
      }
    }

    hideMenu() {
      this.elements.menuContainer.style.overflowY = 'hidden';

      const keyframes = [
        { opacity: 1, visibility: 'visible', offset: 0 },
        { opacity: 0, visibility: 'visible', offset: 0.99 },
        { opacity: 0, visibility: 'hidden', offset: 1 },
      ];

      const options = {
        duration: 150,
        fill: 'both',
      };

      for (let element of this.elements.menus) {
        const animation = element.animate(keyframes, options);
        animation.addEventListener('finish', () => {
          try {
            animation.commitStyles();
          } catch (error) {
            if (error instanceof DOMException) {
              // Fail silently.
            }
          }
          animation.cancel();
        });
      }
    }

    showMenu() {
      this.elements.menuContainer.style.overflowY = null;

      const keyframes = [
        { opacity: 0, visibility: 'hidden', offset: 0 },
        { opacity: 0, visibility: 'visible', offset: 0.01 },
        { opacity: 1, visibility: 'visible', offset: 1 },
      ];

      const options = {
        duration: 150,
        fill: 'both',
      };

      for (let element of this.elements.menus) {
        const animation = element.animate(keyframes, options);
        animation.addEventListener('finish', () => {
          try {
            animation.commitStyles();
          } catch (error) {
            if (error instanceof DOMException) {
              // Fail silently.
            }
          }
          animation.cancel();
        });
      }
    }
  }

  customElements.define('ce-predictive-search', PredictiveSearch);
})();
